import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import pinia from '@/stores'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import {
    isSocialSessionOutcome,
    readSocialReturn,
} from '@/helpers/social-login'

/* TRUSTED */
import AdminAccountScreen from '@/screens/trusted/admin/AccountScreen.vue'
import AdminBillingScreen from '@/screens/trusted/admin/billing/BillingScreen.vue'
import AdminBillingPlansScreen from '@/screens/trusted/admin/billing/PlanPicker.vue'
import AdminProjectsScreen from '@/screens/trusted/admin/ProjectsScreen.vue'
import AdminProjectsSettingsScreen from '@/screens/trusted/admin/ProjectsSettingsScreen.vue'
import AdminProjectsEnvironmentsScreen from '@/screens/trusted/admin/ProjectsEnvironmentsScreen.vue'
import AdminProjectsEnvironmentsSettingsScreen from '@/screens/trusted/admin/ProjectsEnvironmentsSettingsScreen.vue'
import AdminProjectsEnvironmentsWebhooksScreen from '@/screens/trusted/admin/ProjectsEnvironmentsWebhooksScreen.vue'
import AdminUsersScreen from '@/screens/trusted/admin/UsersScreen.vue'
import AdminTeamsScreen from '@/screens/trusted/admin/TeamsScreen.vue'
import AdminTeamDetailsScreen from '@/screens/trusted/admin/TeamDetailsScreen.vue'

import ProfileDefaultScreen from '@/screens/trusted/profile/DefaultScreen.vue'
import ProfilePersonalAccessTokensScreen from '@/screens/trusted/profile/PersonalAccessTokensScreen.vue'
import AdminApiMcpTokensScreen from '@/screens/trusted/admin/ApiMcpTokensScreen.vue'

import ProjectContextDetailScreen from '@/screens/trusted/project/ContextDetailScreen.vue'
import ProjectContextsScreen from '@/screens/trusted/project/ContextsScreen.vue'
import ProjectDebugScreen from '@/screens/trusted/project/DebugScreen.vue'
import ProjectReportsScreen from '@/screens/trusted/project/ReportsScreen.vue'
import ProjectReportsEvaluationVolumeScreen from '@/screens/trusted/project/reports/EvaluationVolume.vue'
import ProjectReportsStaleFlagsScreen from '@/screens/trusted/project/reports/StaleFlags.vue'
import ProjectReportsMissingDefaultValueScreen from '@/screens/trusted/project/reports/MissingDefaultValue.vue'
import ProjectReportsAuditLogScreen from '@/screens/trusted/project/reports/AuditLog.vue'
import ProjectReportsUserActivityScreen from '@/screens/trusted/project/reports/UserActivity.vue'
import ProjectReportsFlagChangesTimelineScreen from '@/screens/trusted/project/reports/FlagChangesTimeline.vue'
import ProjectReportsUserExposureBySegmentScreen from '@/screens/trusted/project/reports/UserExposureBySegment.vue'
import ProjectReportsGeographicExposureScreen from '@/screens/trusted/project/reports/GeographicExposure.vue'
import ProjectReportsRolloutProgressScreen from '@/screens/trusted/project/reports/RolloutProgress.vue'
import ProjectReportsFlagStatusOverviewScreen from '@/screens/trusted/project/reports/FlagStatusOverview.vue'
import ProjectReportsFlagOwnershipCoverageScreen from '@/screens/trusted/project/reports/FlagOwnershipCoverage.vue'
import ProjectSegmentDetailScreen from '@/screens/trusted/project/SegmentDetailScreen.vue'
import ProjectSegmentsScreen from '@/screens/trusted/project/SegmentsScreen.vue'
import ProjectFlagDetailScreen from '@/screens/trusted/project/flags/detail/FlagDetailScreen.vue'
import ProjectFlagsScreen from '@/screens/trusted/project/flags/FlagsScreen.vue'

import WelcomeScreen from '@/screens/trusted/WelcomeScreen.vue'

/* UNTRUSTED */
import ConfirmEmailScreen from '@/screens/untrusted/ConfirmEmailScreen.vue'
import ForgotPasswordScreen from '@/screens/untrusted/ForgotPasswordScreen.vue'
import LoginScreen from '@/screens/untrusted/LoginScreen.vue'
import PaymentAuthenticationScreen from '@/screens/untrusted/PaymentAuthenticationScreen.vue'
import RegisterScreen from '@/screens/untrusted/RegisterScreen.vue'
import ResetPasswordScreen from '@/screens/untrusted/ResetPasswordScreen.vue'

const routes = [
    // TRUSTED - PROJECT
    {
        path: '/:projectKey/:environmentKey/contexts',
        name: 'ProjectContextsScreen',
        component: ProjectContextsScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'CONTEXTS',
            sidebar: true,
            title: 'Contexts',
        },
    },
    {
        path: '/:projectKey/:environmentKey/contexts/:contextUlid',
        name: 'ProjectContextDetailScreen',
        component: ProjectContextDetailScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'CONTEXTS',
            sidebar: true,
            title: 'Contexts',
        },
    },
    {
        path: '/:projectKey/:environmentKey/debug-console',
        name: 'ProjectDebugConsoleScreen',
        component: ProjectDebugScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'DEBUG',
            sidebar: true,
            title: 'Debug Console',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports',
        name: 'ProjectReportsScreen',
        component: ProjectReportsScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/stale-flags',
        name: 'ProjectReportsStaleFlagsScreen',
        component: ProjectReportsStaleFlagsScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Stale Flags - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/missing-default-value',
        name: 'ProjectReportsMissingDefaultValueScreen',
        component: ProjectReportsMissingDefaultValueScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Missing Default Value - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/evaluation-volume',
        name: 'ProjectReportsEvaluationVolumeScreen',
        component: ProjectReportsEvaluationVolumeScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Flag Evaluation Volume - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/audit-log',
        name: 'ProjectReportsAuditLogScreen',
        component: ProjectReportsAuditLogScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Audit Log - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/user-activity',
        name: 'ProjectReportsUserActivityScreen',
        component: ProjectReportsUserActivityScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'User Activity - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/flag-changes-timeline',
        name: 'ProjectReportsFlagChangesTimelineScreen',
        component: ProjectReportsFlagChangesTimelineScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Flag Changes Over Time - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/rollout-progress',
        name: 'ProjectReportsRolloutProgressScreen',
        component: ProjectReportsRolloutProgressScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Rollout Progress - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/user-exposure-by-segment',
        name: 'ProjectReportsUserExposureBySegmentScreen',
        component: ProjectReportsUserExposureBySegmentScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'User Exposure by Segment - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/geographic-exposure',
        name: 'ProjectReportsGeographicExposureScreen',
        component: ProjectReportsGeographicExposureScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Geographic Exposure - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/flag-status-overview',
        name: 'ProjectReportsFlagStatusOverviewScreen',
        component: ProjectReportsFlagStatusOverviewScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Flag Status Overview - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/reports/flag-ownership-coverage',
        name: 'ProjectReportsFlagOwnershipCoverageScreen',
        component: ProjectReportsFlagOwnershipCoverageScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'REPORTS',
            sidebar: true,
            title: 'Flag Ownership & Coverage - Reports',
        },
    },
    {
        path: '/:projectKey/:environmentKey/segments',
        name: 'ProjectSegmentsScreen',
        component: ProjectSegmentsScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'SEGMENTS',
            sidebar: true,
            title: 'Segments',
        },
    },
    {
        path: '/:projectKey/:environmentKey/segments/:segmentUlid',
        name: 'ProjectSegmentDetailScreen',
        component: ProjectSegmentDetailScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'SEGMENTS',
            sidebar: true,
            title: 'Segments',
        },
    },
    {
        path: '/:projectKey/:environmentKey/flags',
        name: 'ProjectFlagsScreen',
        component: ProjectFlagsScreen,
        children: [
            {
                path: ':key',
                name: 'ProjectFlagDetailScreen',
                component: ProjectFlagDetailScreen,
                meta: {
                    admin: false,
                    requiresSession: true,
                    sidebar: true,
                },
            },
        ],
        meta: {
            admin: false,
            requiresSession: true,
            section: 'FLAGS',
            sidebar: true,
            title: 'Flags',
        },
    },

    // TRUSTED - ADMIN
    {
        path: '/admin/account',
        name: 'AdminAccountScreen',
        component: AdminAccountScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Account',
        },
    },
    {
        path: '/admin/billing',
        name: 'AdminBillingScreen',
        component: AdminBillingScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Billing',
        },
    },
    {
        path: '/admin/billing/plans',
        name: 'AdminBillingPlansScreen',
        component: AdminBillingPlansScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Choose a plan',
        },
    },
    {
        path: '/admin/projects',
        name: 'AdminProjectsScreen',
        component: AdminProjectsScreen,
        meta: {
            // Editors are granted project.list/project.read on the backend,
            // so the list itself isn't admin-only — create/update/delete
            // actions are gated within the screen via can().
            admin: false,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Projects',
        },
    },
    {
        path: '/admin/projects/:projectKey/environments',
        name: 'AdminProjectsSingleEnvironmentsScreen',
        component: AdminProjectsEnvironmentsScreen,
        meta: {
            // Backend grants editors environment CRUD (see
            // AuthorizationService::ROLE_PERMISSIONS) — the screen itself
            // gates create/delete actions via can().
            admin: false,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Projects: Environments',
        },
    },
    {
        path: '/admin/projects/:projectKey/environments/:environmentKey/webhooks',
        name: 'AdminProjectsSingleEnvironmentsWebhooksScreen',
        component: AdminProjectsEnvironmentsWebhooksScreen,
        meta: {
            // Backend grants editors webhook CRUD — the screen itself gates
            // create/update/delete actions via can().
            admin: false,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Projects: Environments: Webhooks',
        },
    },
    {
        path: '/admin/projects/:projectKey/environments/:environmentKey/settings',
        name: 'AdminProjectsSingleEnvironmentsSettingsScreen',
        component: AdminProjectsEnvironmentsSettingsScreen,
        meta: {
            // Backend grants editors environment.update — the screen itself
            // gates the save action via can().
            admin: false,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Projects: Environments: Settings',
        },
    },
    {
        path: '/admin/projects/:projectKey/settings',
        name: 'AdminProjectsSingleSettingsScreen',
        component: AdminProjectsSettingsScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Projects: Settings',
        },
    },
    {
        path: '/admin/users',
        name: 'AdminUsersScreen',
        component: AdminUsersScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Users',
        },
    },
    {
        path: '/admin/teams',
        name: 'AdminTeamsScreen',
        component: AdminTeamsScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Teams',
        },
    },
    {
        path: '/admin/teams/:teamUlid',
        name: 'AdminTeamDetailsScreen',
        component: AdminTeamDetailsScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: Team Details',
        },
    },
    // TRUSTED - WELCOME / PERSONALIZATION
    {
        path: '/welcome',
        name: 'WelcomeScreen',
        component: WelcomeScreen,
        meta: {
            requiresSession: true,
            layout: 'welcome',
            title: 'Welcome',
        },
    },

    // TRUSTED - PROFILE
    {
        path: '/profile',
        name: 'ProfileDefaultScreen',
        component: ProfileDefaultScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'PROFILE',
            sidebar: false,
            title: 'User Profile',
        },
    },

    // TRUSTED - PROFILE - PERSONAL ACCESS TOKENS (hidden if the user's
    // personal_access_tokens_enabled indicator is off)
    {
        path: '/profile/personal-access-tokens',
        name: 'ProfilePersonalAccessTokensScreen',
        component: ProfilePersonalAccessTokensScreen,
        meta: {
            admin: false,
            requiresSession: true,
            section: 'PROFILE',
            sidebar: false,
            title: 'Personal Access Tokens',
        },
        beforeEnter: (_to, _from, next) => {
            const enabled =
                useSessionStore(pinia).data?.user?.data
                    ?.personal_access_tokens_enabled !== false
            if (!enabled) {
                next({ name: 'ProfileDefaultScreen' })
            } else {
                next()
            }
        },
    },

    // TRUSTED - ADMIN - API/MCP TOKENS
    {
        path: '/admin/api-mcp-tokens',
        name: 'AdminApiMcpTokensScreen',
        component: AdminApiMcpTokensScreen,
        meta: {
            admin: true,
            requiresSession: true,
            section: 'ADMIN',
            sidebar: false,
            title: 'Admin: API/MCP Tokens',
        },
    },

    // Legacy paths — redirect to the merged API/MCP Tokens screen
    {
        path: '/admin/api-tokens',
        redirect: { name: 'AdminApiMcpTokensScreen' },
    },
    {
        path: '/admin/personal-access-tokens',
        redirect: { name: 'AdminApiMcpTokensScreen' },
    },

    // UNTRUSTED
    {
        path: '/confirm-email/:ulid/:token',
        name: 'ConfirmEmailScreen',
        component: ConfirmEmailScreen,
        meta: {
            title: 'Confirm Your Email',
        },
    },
    {
        path: '/forgot-password',
        name: 'ForgotPasswordScreen',
        component: ForgotPasswordScreen,
        meta: {
            title: 'Forgot Password',
        },
    },
    {
        path: '/login',
        name: 'LoginScreen',
        component: LoginScreen,
        meta: {
            title: 'Sign Into Your Account',
        },
    },
    {
        path: '/payment/authenticate/:paymentUlid',
        name: 'PaymentAuthenticationScreen',
        component: PaymentAuthenticationScreen,
        meta: {
            // The link in the 3-D Secure email is for whoever handles billing, often the
            // owner, who is usually signed in. It carries its own signature, so signed in
            // or out it opens the same page.
            allowWhenLoggedIn: true,
            title: 'Confirm Your Payment',
        },
    },
    {
        path: '/register',
        name: 'RegisterScreen',
        component: RegisterScreen,
        meta: {
            title: 'Register for an Account',
        },
    },
    {
        path: '/reset-password/:token',
        name: 'ResetPasswordScreen',
        component: ResetPasswordScreen,
        meta: {
            title: 'Reset Your Password',
        },
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes,
})

/**
 * Sync projectKey and environmentKey route params into the session store so
 * that the active context is preserved after a page refresh.
 *
 * Exported for unit testing.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {ReturnType<typeof import('@/stores/session').useSessionStore>} sessionStore
 * @returns {void}
 */
export function syncContextFromRouteParams(to, sessionStore) {
    if (to.params.projectKey && to.params.environmentKey) {
        if (sessionStore.data?.current) {
            sessionStore.data.current.projectKey = to.params.projectKey
            sessionStore.data.current.environmentKey = to.params.environmentKey
        }
    }
}

/**
 * Where someone goes who just came back from GitHub or Google signed in.
 *
 * The API ends a social sign-in with a redirect to `/login?social=signed_in`
 * (or `signed_up`) with the session cookie already set. By the time this runs
 * the session has been hydrated, and a signed-in visitor to the login screen
 * would otherwise be sent to the flags screen. A password login goes through
 * the welcome screen first, and so does this, which also picks up any pending
 * `redirectAfterLogin`.
 *
 * Exported for unit testing.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {boolean} isLoggedIn
 * @returns {{ name: string }|null} The location to redirect to, or null when
 *   this isn't a social sign-in return
 */
export function socialSignInRedirect(to, isLoggedIn) {
    if (!isLoggedIn || to.name !== 'LoginScreen') {
        return null
    }

    const socialReturn = readSocialReturn(to.query)

    return socialReturn !== null && isSocialSessionOutcome(socialReturn.outcome)
        ? { name: 'WelcomeScreen' }
        : null
}

router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore(pinia)
    await authStore.hydrate()
    const isLoggedIn = authStore.status.loggedIn

    if (isLoggedIn) {
        syncContextFromRouteParams(to, useSessionStore(pinia))
    }

    const socialRedirect = socialSignInRedirect(to, isLoggedIn)

    if (socialRedirect !== null) {
        next(socialRedirect)
    } else if (to.path == '/' && !isLoggedIn) {
        next({ path: '/login' })
    } else if (to.path == '/' && isLoggedIn) {
        // Redirect to the first project's flags screen
        const current = authStore.user?.current
        if (current) {
            next({ name: 'ProjectFlagsScreen', params: current })
        } else {
            next({ name: 'LoginScreen' })
        }
    } else if (
        to.matched.some((record) => record.meta.requiresSession) &&
        !isLoggedIn
    ) {
        sessionStorage.setItem('redirectAfterLogin', to.fullPath)
        next({ path: '/login', query: { t: '1' } })
    } else if (
        to.matched.some((record) => !record.meta.requiresSession) &&
        !to.matched.some((record) => record.meta.allowWhenLoggedIn) &&
        isLoggedIn
    ) {
        // Already logged in, redirect to flags screen
        const current = authStore.user?.current
        if (current) {
            next({ name: 'ProjectFlagsScreen', params: current })
        } else {
            next()
        }
    } else if (
        to.matched.some((record) => record.meta.admin) &&
        !['admin', 'owner'].includes(authStore.user?.user?.data?.effective_role)
    ) {
        // Not admin, redirect to flags screen
        const current = authStore.user?.current
        if (current) {
            next({ name: 'ProjectFlagsScreen', params: current })
        } else {
            next({ name: 'LoginScreen' })
        }
    } else {
        next()
    }
})

const DEFAULT_TITLE = ' | Zenmanage'
router.afterEach((to, _from) => {
    nextTick().then(() => {
        const pageTitle =
            to.meta && to.meta.title
                ? to.meta.title + DEFAULT_TITLE
                : DEFAULT_TITLE
        document.title = pageTitle
    })
})

export default router
